/**
 * Pipeline Stage Icon Component
 *
 * Renders the appropriate icon for a pipeline stage status.
 */

import {
  CheckCircle,
  Circle,
  Loader,
  SkipForward,
  XCircle,
} from 'lucide-react';
import type { StageStatus } from '../../types/pipeline';

interface PipelineStageIconProps {
  status: StageStatus;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
} as const;

const STATUS_COLORS: Record<StageStatus, string> = {
  completed: 'text-accent-green',
  active: 'text-accent-yellow',
  pending: 'text-text-muted/40',
  skipped: 'text-accent-blue',
  failed: 'text-accent-red',
};

export function PipelineStageIcon({
  status,
  size = 'md',
}: PipelineStageIconProps) {
  const sizeClass = SIZE_CLASSES[size];
  const colorClass = STATUS_COLORS[status];
  const className = `${sizeClass} ${colorClass}`;

  switch (status) {
    case 'completed':
      return <CheckCircle className={className} />;
    case 'active':
      return <Loader className={`${className} animate-spin`} />;
    case 'pending':
      return <Circle className={className} />;
    case 'skipped':
      return <SkipForward className={className} />;
    case 'failed':
      return <XCircle className={className} />;
    default:
      return <Circle className={className} />;
  }
}
